import { useState, useCallback } from "react";
import axios from "axios"; // Import axios for making HTTP requests

export const useVinScanner = () => {
  // State to store the detected VIN
  const [vin, setVin] = useState("");
  // State to indicate if the VIN scanning process is ongoing
  const [isLoading, setIsLoading] = useState(false);
  // State to store any errors during the process
  const [error, setError] = useState(null);

  /**
   * Handles the process of capturing the photo and extracting the VIN using the VINbarcode API.
   * @param {string} dataUri - The captured image in Data URI format.
   */
  const handleTakePhoto = useCallback((dataUri) => {
    console.log("handleTakePhoto called with dataUri length:", dataUri.length);
    setIsLoading(true);
    setError(null); // Clear any previous errors

    try {
      // Convert dataUri to Blob
      console.log("Converting dataUri to blob...");
      console.log("DataUri preview:", dataUri.substring(0, 100) + "...");

      // Check if dataUri is valid
      if (!dataUri || !dataUri.includes(',')) {
        throw new Error("Invalid dataUri format");
      }

      const parts = dataUri.split(',');
      if (parts.length !== 2) {
        throw new Error("DataUri does not contain proper base64 data");
      }

      const header = parts[0]; // data:image/jpeg;base64
      const base64Data = parts[1];

      // Extract MIME type more safely
      const mimeMatch = header.match(/data:([^;]+)/);
      const mimeString = mimeMatch ? mimeMatch[1] : 'image/jpeg';
      console.log("MIME type:", mimeString);
      console.log("Base64 data length:", base64Data.length);

      // Convert base64 to blob using a more reliable method
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeString });

      console.log("Blob created successfully:");
      console.log("- Size:", blob.size, "bytes");
      console.log("- Type:", blob.type);

      // Verify blob is not empty
      if (blob.size === 0) {
        throw new Error("Created blob is empty");
      }

      const formData = new FormData();
      formData.append('image', blob, 'vin_image.jpg');

      // Log FormData contents for debugging
      console.log("FormData created with entries:");
      for (let pair of formData.entries()) {
        console.log("- Key:", pair[0], "Value type:", typeof pair[1], "Size:", pair[1].size || "unknown");
      }

      console.log("Making API call...");

      axios.post('https://www.recognition.ws/vinbarcode/v2?accesscode=1e388618-7f73-4e70-818e-2fa4edf22cad&vindecode=TRUE&format=JSON', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 30000 // 30 second timeout
      })
      .then(response => {
        console.log("API response received:", response);
        const result = response.data;
        console.log("API result:", result);

        if (result.status === 'SUCCESS') {
          console.log("VIN detected:", result.vin_captured);
          setVin(result.vin_captured);
        } else {
          console.log("VIN detection failed:", result.message);
          setError(`Failed to detect VIN: ${result.message || 'Please try again.'}`);
        }
      })
      .catch(error => {
        console.error("API Error details:", error);
        console.error("Error response:", error.response);
        console.error("Error message:", error.message);

        if (error.code === 'ECONNABORTED') {
          setError("Request timed out. Please try again.");
        } else if (error.response) {
          setError(`Server error: ${error.response.status}. Please try again.`);
        } else {
          setError("Network error. Please check your connection and try again.");
        }
      })
      .finally(() => {
        console.log("API call completed, setting loading to false");
        setIsLoading(false);
      });
    } catch (conversionError) {
      console.error("Error converting image:", conversionError);
      setError("Error processing image data. Please try again.");
      setIsLoading(false);
    }
  }, []);

  return {
    vin,
    isLoading,
    error,
    handleTakePhoto,
  };
};
